/**
 * The Insights page's contract with `/api/insights/*` (REC-854, ADR §10), and the few
 * formatters only this page needs.
 *
 * Side keys are the API's own, `resell` / `trade-in`: a side the customer has no
 * competitors on comes back `null`, and the page hides it rather than showing zeros.
 * Percentages arrive as 0–100 at two decimals.
 */

export type InsightsSide = 'resell' | 'trade-in'

export const INSIGHTS_SIDES: InsightsSide[] = ['resell', 'trade-in']

/** The `sideConfig` key a side is worded and coloured by elsewhere in the app. */
export const sideConfigKey = (side: InsightsSide): 'resell' | 'buyback' => (side === 'resell' ? 'resell' : 'buyback')

/**
 * A side's hue for a canvas chart, from the same CSS token its badges use
 * (`--resell` / `--tradein`), so the charts follow the theme's palette.
 */
export const sideColor = (side: InsightsSide, alpha = 1): string => {
    const token = side === 'resell' ? '--resell' : '--tradein'
    const hsl = window.getComputedStyle(window.document.documentElement).getPropertyValue(token).trim()

    return hsl === '' ? `rgba(156, 163, 175, ${alpha})` : `hsl(${hsl} / ${alpha})`
}

export type InsightsRange = '90d' | '1y' | 'all'

export const INSIGHTS_RANGES: InsightsRange[] = ['90d', '1y', 'all']

export type InsightsView = 'model' | 'competitor'

export type InsightsOption = {
    code: string
    label: string
}

export type InsightsRegion = {
    geo: string
    label: string
    currency: string
    countries: Array<{ geo: string; label: string }>
}

export type InsightsFilterOptions = {
    /** The Monday the page shows; null until a week has been materialised. */
    week: string | null
    sides: InsightsSide[]
    regions: InsightsRegion[]
    brands: Array<{ id: number; name: string }>
    lines: InsightsOption[]
    tiers: InsightsOption[]
    storages: InsightsOption[]
    grades: InsightsOption[]
}

/** The scope every data endpoint takes; an absent level means "All". */
export type InsightsScope = {
    geo: string
    brand_id: number
    line: string | null
    tier: string | null
    storage: string | null
    grade: string | null
}

export type InsightsDepreciation = {
    value: number | null
    coverage_pct: number | null
}

export type InsightsSideSummary = {
    trend_average: number | null
    pct_of_launch: number | null
    depreciation_60d: InsightsDepreciation
    volatility_60d: { value: number | null; links: number }
    competitors: number
    listings: number
}

export type InsightsSummary = {
    week: string | null
    currency: string
    resell: InsightsSideSummary | null
    'trade-in': InsightsSideSummary | null
    margin: { value: number | null; pct_of_resale: number | null; coverage_pct: number | null } | null
}

export type InsightsChartPoint = {
    date: string
    trend_average: number | null
    listings: number
}

export type InsightsChart = {
    week: string | null
    currency: string
    range: InsightsRange
    resell: InsightsChartPoint[] | null
    'trade-in': InsightsChartPoint[] | null
}

export type InsightsDevice = {
    rank: number
    line: string
    tier: string
    storage: string
    label: string
    listings: number
    share_pct: number | null
    trend_average: number | null
    depreciation_60d: InsightsDepreciation
}

export type InsightsTopDevices = {
    week: string | null
    currency: string
    /** Null for a customer without resale competitors: the ranking is resale only. */
    devices_in_view: number | null
    devices: InsightsDevice[] | null
}

export type InsightsCompetitor = {
    vendor_id: number
    name: string
    country: string
    avg: number | null
    vs_market_pct: number | null
    vs_you_pct: number | null
    share_pct: number | null
    listings: number
    grade_mix_pct: Record<'N' | 'A' | 'B' | 'C', number>
    your_skus: { matched: number; of: number } | null
    coverage: {
        skus: number
        of: number
        newest_generation: number | null
        oldest_generation: number | null
    }
    most_aggressive: boolean
}

export type InsightsYou = {
    avg: number | null
    vs_market_pct: number | null
    listings: number
    your_skus: number
}

export type InsightsCompetitorSide = {
    you: InsightsYou | null
    competitors: InsightsCompetitor[]
}

export type InsightsCompetitors = {
    week: string | null
    currency: string
    resell: InsightsCompetitorSide | null
    'trade-in': InsightsCompetitorSide | null
}

/** The query string of a scope, leaving out the levels set to "All". */
export const scopeQuery = (scope: InsightsScope, extra: Record<string, string> = {}): string => {
    const params = new URLSearchParams({ geo: scope.geo, brand_id: String(scope.brand_id), ...extra })

    for (const key of ['line', 'tier', 'storage', 'grade'] as const) {
        const value = scope[key]

        if (value !== null) {
            params.set(key, value)
        }
    }

    return params.toString()
}

/**
 * The symbol of a currency code, from the platform rather than a table of our own.
 *
 * The Insights payloads carry only the code (a scope is in one currency), while the
 * rest of the app formats money as "symbol amount" with the symbol the backend sends;
 * this keeps the page reading the same way.
 */
export const currencySymbol = (currency: string | null | undefined): string => {
    if (!currency) {
        return ''
    }

    try {
        const parts = new Intl.NumberFormat('en', {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol',
        }).formatToParts(0)

        return parts.find((part) => part.type === 'currency')?.value ?? currency
    } catch {
        return currency
    }
}

/** A whole count with thousands separators. */
export const formatCount = (value: number | null | undefined, locale: string): string => {
    if (value === null || value === undefined) {
        return '—'
    }

    return Math.round(value).toLocaleString(locale)
}

/** A 0–100 share, unsigned. */
export const formatPct = (value: number | null | undefined, digits = 0): string => {
    if (value === null || value === undefined) {
        return '—'
    }

    return `${value.toFixed(digits)}%`
}

/** A comparison against a reference, as ▲ / ▼ and the absolute gap. */
export const formatDelta = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
        return '—'
    }

    if (value === 0) {
        return '0.0%'
    }

    return `${value > 0 ? '▲' : '▼'} ${Math.abs(value).toFixed(1)}%`
}

/**
 * The generations a competitor's SKUs span, newest first: N is the newest line in the
 * view, N−k the line k generations older.
 */
export const formatGenerations = (newest: number | null, oldest: number | null, to: string): string | null => {
    if (newest === null || oldest === null) {
        return null
    }

    const label = (rank: number) => (rank === 0 ? 'N' : `N−${rank}`)

    return newest === oldest ? label(newest) : `${label(newest)} ${to} ${label(oldest)}`
}
